import { useEffect, useMemo, useRef, useState } from 'react';
import {
  EMPTY,
  loadNavigationRegistry,
  normalisePath,
  peekNavigationRegistry,
} from '../services/navigationRegistry';
import { primePermissionRules } from '../services/permissionRules';

/**
 * useNavigationGating — the administrator's page gating, overlaid on the
 * compiled sidebar (RBAC Phase 2).
 *
 * Gating a page used to be a code change in `ModernMainLayout.js`. It is now a
 * row in `users.NavigationItem`, and this hook is how the sidebar reads it.
 *
 * **The registry gates; the code still renders.** The sidebar keeps its literal
 * array for labels, icons and structure — this hook answers only *may this user
 * see that entry*. That split is deliberate: no icon-name registry to keep in
 * step, and nothing about rendering can break when the registry is missing.
 *
 * **The last rung is non-negotiable.** Unmigrated tenant, unseeded table,
 * failed request, entry not in the registry — every one of those falls back to
 * today's rule (`!item.permission || hasPermission(item.permission)`). Making
 * gating configurable must not give the sidebar a new way to fail.
 *
 * **Not the security boundary.** The API is authoritative: `HasModuleAccess`
 * guards the data regardless of what the menu shows. Setting a page to
 * "visible to everyone" exposes nothing by itself.
 *
 * Keys must match `scripts/extract_navigation.py` and `users.NavigationItem`:
 *
 *     top-level item   ->  <item.key>                  e.g. "crm"
 *     child of an item ->  <item.key>:<route>          e.g. "crm:/crm/leads"
 *     nth repeat       ->  <item.key>:<route>#<n>      n = 2, 3, ...
 */

/** Key for a child entry. `occurrence` is 1-based, counting repeats of the SAME
 *  route under the SAME parent — see the `#n` note above. */
export const childNavKey = (parentKey, route, occurrence = 1) =>
  `${parentKey}:${route}${occurrence > 1 ? `#${occurrence}` : ''}`;

/**
 * Number the children of one menu item exactly as the extractor does, so both
 * sides agree when a route appears twice under one parent (today: "Supplier
 * Onboarding" and "PO Acknowledgements" both point at /supplier-onboarding).
 *
 * @param {Array} children - submenu entries, in the order the sidebar declares
 * @returns {Map} child object -> key
 */
export const buildChildKeys = (parentKey, children) => {
  const seen = new Map();
  const keys = new Map();
  (children || []).forEach((child) => {
    const route = child?.path;
    if (!route) return;
    const n = (seen.get(route) || 0) + 1;
    seen.set(route, n);
    keys.set(child, childNavKey(parentKey, route, n));
  });
  return keys;
};

const useNavigationGating = () => {
  // The payload is fetched once per session and shared with the router's gate
  // (Phase 3), so a remount of the sidebar costs nothing.
  const preloaded = peekNavigationRegistry();
  const [state, setState] = useState(preloaded || EMPTY);
  const [loaded, setLoaded] = useState(Boolean(preloaded));
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    // Phase 5: pull the implied-permission rule from the server at the same
    // time. It is primed HERE rather than in `usePermissions` because that hook
    // runs during render, hundreds of times per page, and has no re-render of
    // its own to trigger once the answer lands — this effect does, so the
    // sidebar re-evaluates with the served rule in the same pass. Never
    // rejects; until it resolves, `usePermissions` uses its own copy.
    primePermissionRules();
    // `loadNavigationRegistry` never rejects — a failure resolves to EMPTY and
    // the sidebar behaves exactly as it did before Phase 2.
    loadNavigationRegistry().then((data) => {
      if (cancelled.current) return;
      setState(data);
      setLoaded(true);
    });
    return () => {
      cancelled.current = true;
    };
  }, []);

  return useMemo(() => {
    const { configured, decisions, routes, reasons } = state;
    const labels = state.labels || {};

    /**
     * The registry's verdict for one entry, or `null` when it has none — the
     * caller must then apply its own hardcoded permission check.
     *
     * `null` rather than `true` is the whole safety property: an entry the
     * registry has never heard of must keep its compiled gating, not become
     * visible to everyone.
     */
    const decide = (key, route) => {
      if (!configured) return null;
      if (key && Object.prototype.hasOwnProperty.call(decisions, key)) return decisions[key];
      // `routes` is keyed by normalised path (Phase 3), so normalise the lookup
      // or a sidebar path written with a trailing slash would silently miss.
      const path = route ? normalisePath(route) : null;
      if (path && Object.prototype.hasOwnProperty.call(routes, path)) return routes[path];
      return null;
    };

    /** `decide`, with the compiled fallback applied. `fallback` is a thunk so
     *  the permission check is skipped when the registry already answered. */
    const allows = (key, route, fallback) => {
      const verdict = decide(key, route);
      return verdict === null ? Boolean(fallback && fallback()) : verdict;
    };

    return {
      configured,
      loaded,
      decide,
      allows,
      /** The tenant's display name for an entry (RBAC → Pages → Display name),
       *  else `fallback` — the compiled label. Presentation only; gating is
       *  unaffected. */
      labelFor: (key, fallback) => (key && labels[key]) || fallback,
      /** Why an entry was allowed or denied — for diagnostics, not display. */
      reasonFor: (key) => reasons[key] || null,
      childNavKey,
      buildChildKeys,
    };
  }, [state, loaded]);
};

export default useNavigationGating;
